import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useApi from '../_hooks/useApi';
import Select from '../CommonComponents/Select';
import LoadingMask from '../CommonComponents/LoadingMask';
import { Alert } from '../CommonComponents/Alert/Alert';
import {
  ButtonsContainer,
  CloseButton,
  DropdownButton,
  InlineContent,
  ListContainer,
  ListItem,
  ListTitle,
  Name,
  NameNote,
  RelationType,
  RemoveButton,
  SearchButton,
  StyledInput,
  Title,
  Wrapper,
} from './SharingSettingsStyles';
import { PrimaryButton, SecondaryButton } from '../CommonComponents/Button';
import {
  isDemoSelector,
  isDisableAdviserSuggestionsSelector,
} from '../AdviserArea/AdviserAreaSelector';
import { Menu, MenuContent, MenuItem, MenuTrigger } from '../CommonComponents/OPL/Menu/Menu';
import { ListItem as ListItemOPL } from '../CommonComponents/OPL/List/ListItem';
import { Icon } from '../CommonComponents/OPL/Icon/Icon';
import { refetchCurrentClient } from '../AdviserArea/AdviserAreaActions';

interface Adviser {
  AdviserId: number;
  FirstName: string;
  IsOwner: boolean;
  IsRemovable: boolean;
  LastName: string;
  IsManager: boolean;
}

interface Props {
  clientId: string;
  onReturn: () => {};
}

enum Action {
  remove,
  changeOwnership,
}

interface SelectedAdviser {
  id?: number;
  action?: Action;
}

const adviserOptionsWithoutClientRelationship = (dropdownOptions, clientRelationship) =>
  dropdownOptions.filter(
    option => !clientRelationship.some(({ AdviserId }) => option.id === AdviserId),
  );

const adviserDropDownOptions = advisers => {
  return advisers.map(({ AdviserFirstName, AdviserLastName, AdviserEmail, AdviserId }) => ({
    id: AdviserId,
    label: `${AdviserFirstName} ${AdviserLastName} - ${AdviserEmail}`,
    value: AdviserEmail,
  }));
};

export const SharingSettings: React.FC<Props> = ({ clientId, onReturn }) => {
  const dispatch = useDispatch();

  const [dropdownOptions, setDropdownOptions] = useState([]);
  const [clientRelationship, setClientRelationship] = useState<Adviser[]>([]);
  const [canTransferOwnership, setCanTransferOwnership] = useState(false);
  const [selectedAdvisor, setSelectedAdvisor] = useState<SelectedAdviser>({});
  const [inputValue, setInputValue] = useState<string>('');

  const isDisableAdviserSuggestions = useSelector(isDisableAdviserSuggestionsSelector);
  const isDemo = useSelector(isDemoSelector);

  const [findAdvisorMessage, setFindAdvisorMessage] = useState<{
    message: string[];
    type: 'info' | 'danger' | 'success';
  }>();

  const [
    fetchSharingSettings,
    { loading: isLoadingFetchSharingSettings, errors: errorsFetchSharingSettings },
  ] = useApi(`/Adviser/SharingSettings/?clientId=${clientId}`);

  const [
    fetchAdvisersInSameOrganization,
    {
      loading: isLoadingFetchAdvisersInSameOrganization,
      errors: errorsFetchAdvisersInSameOrganization,
    },
  ] = useApi(`/Adviser/Adviser/GetAdvisersInSameOrganization`);

  const [
    requestTransferOwnership,
    { loading: loadingRequestTransferOwnership, errors: errorsRequestTransferOwnership },
  ] = useApi(`/Adviser/SharingSettings/TransferOwnership`, {
    method: 'POST',
  });

  const [
    requestUnShareClientAccess,
    { loading: loadingRequestUnShareClientAccess, errors: errorsRequestUnShareClientAccess },
  ] = useApi(`/Adviser/SharingSettings/UnShareClientAccess`, {
    method: 'POST',
  });

  const [
    shareClientAccess,
    { loading: isLoadingShareClientAccess, errors: errorsShareClientAccess },
  ] = useApi(`/Adviser/SharingSettings/ShareClientAccess`, {
    method: 'POST',
  });

  useEffect(() => {
    if (!isDisableAdviserSuggestions) {
      fetchAdvisersInSameOrganization({
        onSuccess: ({ Advisers }) => {
          setDropdownOptions(adviserDropDownOptions(Advisers));
        },
      });
    }
    fetchSharingSettings({
      onSuccess: ({ Relationships, CanTransferOwnership }) => {
        setCanTransferOwnership(CanTransferOwnership);
        setClientRelationship(Relationships);
      },
    });
  }, []);

  const [requestFindAdviser, { loading: loadingFindAdviser }] = useApi(`/Adviser/Adviser/Find`, {
    method: 'POST',
  });

  const handleClientSharingSuccess = useCallback(
    (advisorsAdded: Adviser[], errors?: string[]) => {
      setFindAdvisorMessage({
        type: errors ? 'danger' : 'success',
        message: errors || ['Advisor(s) successfully shared'],
      });

      const newAdvisorsWithAccess = advisorsAdded.filter(
        ({ AdviserId }) =>
          !clientRelationship.some(advisorWithAccess => advisorWithAccess.AdviserId === AdviserId),
      );

      setClientRelationship([...clientRelationship, ...newAdvisorsWithAccess]);
    },
    [clientRelationship],
  );

  const findAdviser = ({ email }) => {
    setFindAdvisorMessage(undefined);

    requestFindAdviser({
      body: { email },
      onSuccess: (advisorsFound: Adviser[]) => {
        const advisorsFoundWithoutAccess = advisorsFound.filter(
          advisorFound =>
            !clientRelationship.some(
              advisorWithAccess => advisorWithAccess.AdviserId === advisorFound.AdviserId,
            ),
        );

        if (advisorsFoundWithoutAccess.length > 0) {
          shareClientAccess({
            queryParams: {
              clientId,
              adviserEmail: inputValue,
            },
            onSuccess: handleClientSharingSuccess,
            onError: errorList => setFindAdvisorMessage({ type: 'danger', message: errorList }),
          });
        } else {
          setFindAdvisorMessage({ type: 'info', message: ['Advisor already with access'] });
        }
      },
      onError: errorList => setFindAdvisorMessage({ type: 'danger', message: errorList }),
    });
  };

  const errorList = [
    ...errorsFetchSharingSettings,
    ...errorsFetchAdvisersInSameOrganization,
    ...errorsRequestTransferOwnership,
    ...errorsRequestUnShareClientAccess,
    ...errorsShareClientAccess,
  ];

  const adviserOptions = adviserOptionsWithoutClientRelationship(
    dropdownOptions,
    clientRelationship,
  );

  if (selectedAdvisor.id) {
    return (
      <Wrapper>
        <Title>Sharing Settings</Title>
        {errorList.length > 0 && <Alert message={errorList} />}
        {selectedAdvisor.action === Action.changeOwnership ? (
          <ListTitle>Are you sure you want to change the owner of this client?</ListTitle>
        ) : (
          <ListTitle>
            Are you sure you want to remove this Advisor from accessing this client?
          </ListTitle>
        )}
        <ButtonsContainer>
          <SecondaryButton
            onClick={() => {
              setSelectedAdvisor({});
            }}
          >
            Cancel
          </SecondaryButton>
          <PrimaryButton
            isLoading={loadingRequestTransferOwnership || loadingRequestUnShareClientAccess}
            disabled={loadingRequestTransferOwnership || loadingRequestUnShareClientAccess}
            onClick={() => {
              if (selectedAdvisor.action === Action.changeOwnership) {
                requestTransferOwnership({
                  queryParams: {
                    clientId,
                    newAdviserId: selectedAdvisor.id as number,
                  },
                  onSuccess: () => {
                    setClientRelationship([]);
                    setSelectedAdvisor({});
                    fetchSharingSettings({
                      onSuccess: ({ Relationships, CanTransferOwnership }) => {
                        setCanTransferOwnership(CanTransferOwnership);
                        setClientRelationship(Relationships);
                      },
                    });
                    dispatch(refetchCurrentClient(clientId, isDemo));
                  },
                });
              } else {
                requestUnShareClientAccess({
                  queryParams: {
                    clientId,
                    adviserId: selectedAdvisor.id as number,
                  },
                  onSuccess: () => {
                    setClientRelationship(
                      clientRelationship.filter(
                        (client: Adviser) => client.AdviserId !== selectedAdvisor.id,
                      ),
                    );
                    setSelectedAdvisor({});
                  },
                });
              }
            }}
          >
            Yes I&apos;m Sure
          </PrimaryButton>
        </ButtonsContainer>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <CloseButton appendIcon="fa fa-times" onClick={onReturn} />
      <Title>Sharing Settings</Title>
      {errorList.length > 0 && <Alert message={errorList} />}

      {isLoadingFetchSharingSettings && (
        <>
          <br />
          <LoadingMask isDark width="100%" height="80px" />
          <br />
        </>
      )}

      {clientRelationship.length > 0 && (
        <>
          <ListTitle>Who has access</ListTitle>
          <ListContainer>
            {clientRelationship.map(
              ({ IsOwner, AdviserId, FirstName, LastName, IsRemovable, IsManager }) => {
                if (selectedAdvisor === AdviserId) {
                  return <LoadingMask key={AdviserId} isDark width="100%" height="48px" />;
                }
                const currentLabel = IsOwner ? 'Owner' : 'Advisor';
                const options = [
                  { value: true, label: 'Owner' },
                  { value: false, label: 'Advisor' },
                ].filter(({ value }) => value !== IsOwner);

                return (
                  <ListItem key={AdviserId}>
                    <Name>
                      {FirstName} {LastName}
                      <NameNote>{IsManager ? 'Manager' : ''}</NameNote>
                    </Name>
                    {IsOwner || !canTransferOwnership ? (
                      <RelationType>{currentLabel}</RelationType>
                    ) : (
                      <Menu>
                        <MenuTrigger>
                          <DropdownButton>
                            {currentLabel} <Icon className="fas fa-caret-down" />
                          </DropdownButton>
                        </MenuTrigger>

                        <MenuContent
                          onKeyDown={evt => {
                            if (evt.key === 'Escape') {
                              // Prevent closing the modal when pressing escape to close the menu
                              evt.stopPropagation();
                            }
                          }}
                        >
                          {options.map(({ label }) => (
                            <MenuItem
                              onSelect={() =>
                                setSelectedAdvisor({
                                  id: AdviserId,
                                  action: Action.changeOwnership,
                                })
                              }
                            >
                              <ListItemOPL>{label}</ListItemOPL>
                            </MenuItem>
                          ))}
                        </MenuContent>
                      </Menu>
                    )}

                    <RemoveButton
                      isRemovable={IsRemovable}
                      appendIcon="fa fa-times"
                      onClick={() => {
                        if (IsRemovable) {
                          setSelectedAdvisor({
                            id: AdviserId,
                            action: Action.remove,
                          });
                        }
                      }}
                    />
                  </ListItem>
                );
              },
            )}
          </ListContainer>
        </>
      )}

      {isLoadingFetchAdvisersInSameOrganization && (
        <>
          <LoadingMask isDark width="100%" height="80px" />
          <br />
        </>
      )}

      <>
        <ListTitle>Invite Advisors</ListTitle>
        {isLoadingShareClientAccess ? (
          <LoadingMask isDark width="100%" height="46px" />
        ) : (
          <>
            {findAdvisorMessage && Object.keys(findAdvisorMessage).length > 0 && (
              <Alert
                shouldScrollOnUpdate={false}
                message={findAdvisorMessage.message}
                appearance={findAdvisorMessage.type}
              />
            )}
            {isDisableAdviserSuggestions ? (
              <InlineContent>
                <StyledInput
                  // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
                  // @ts-ignore
                  isLoading={loadingFindAdviser}
                  disabled={loadingFindAdviser}
                  placeholder="Advisor email"
                  onChange={({ target: { value } }) => {
                    setInputValue(value);
                  }}
                />
                <SearchButton
                  appendIcon="fas fa-user-plus"
                  isLoading={loadingFindAdviser}
                  disabled={loadingFindAdviser}
                  onClick={() => {
                    findAdviser({ email: inputValue });
                  }}
                />
              </InlineContent>
            ) : (
              adviserOptions.length > 0 && (
                <Select
                  options={adviserOptions}
                  placeholder="Select Advisor"
                  onChange={({ value: adviserEmail }) => {
                    shareClientAccess({
                      queryParams: {
                        clientId,
                        adviserEmail,
                      },
                      onSuccess: handleClientSharingSuccess,
                    });
                  }}
                />
              )
            )}
          </>
        )}
      </>
    </Wrapper>
  );
};
